import { useState, useRef, useEffect } from 'react';
import { ArrowLeft, X, Plus, Phone, Mail, MessageSquare, Car, Check, Loader2, Image, ChevronDown, ChevronUp, Gauge, Settings } from 'lucide-react';
import { Button } from './ui/button';
import PriceModePicker from './PriceModePicker';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CAR_MAKES = ['Toyota', 'Nissan', 'Honda', 'Mercedes-Benz', 'BMW', 'Audi', 'Lexus', 'Land Rover', 'Porsche', 'Ford', 'Chevrolet', 'Hyundai', 'Kia', 'Mazda', 'Mitsubishi', 'Volkswagen', 'Jeep', 'Dodge', 'GMC', 'Infiniti', 'Cadillac', 'Ferrari', 'Lamborghini', 'Bentley', 'Rolls-Royce', 'Maserati', 'MG', 'Chery', 'Geely', 'BYD', 'Tesla', 'Other'];
const BODY_TYPES = [{ value: 'sedan', label: 'سيدان', labelEn: 'Sedan' }, { value: 'suv', label: 'SUV', labelEn: 'SUV' }, { value: 'coupe', label: 'كوبيه', labelEn: 'Coupe' }, { value: 'hatchback', label: 'هاتشباك', labelEn: 'Hatchback' }, { value: 'convertible', label: 'مكشوفة', labelEn: 'Convertible' }, { value: 'pickup', label: 'بيك أب', labelEn: 'Pickup' }, { value: 'van', label: 'فان', labelEn: 'Van' }];
const SPECS = [{ value: 'gcc', label: 'خليجي', labelEn: 'GCC' }, { value: 'american', label: 'أمريكي', labelEn: 'American' }, { value: 'european', label: 'أوروبي', labelEn: 'European' }, { value: 'japanese', label: 'ياباني', labelEn: 'Japanese' }];

const CAR_FEATURES = {
  exterior: { title: 'الخارجية', titleEn: 'Exterior', items: [{ id: 'alloy_wheels', label: 'جنوط ألمنيوم', labelEn: 'Alloy Wheels' }, { id: 'sunroof', label: 'فتحة سقف', labelEn: 'Sunroof' }, { id: 'panoramic_roof', label: 'سقف بانورامي', labelEn: 'Panoramic Roof' }, { id: 'led_headlights', label: 'مصابيح LED', labelEn: 'LED Headlights' }, { id: 'fog_lights', label: 'مصابيح ضباب', labelEn: 'Fog Lights' }, { id: 'rear_spoiler', label: 'جناح خلفي', labelEn: 'Rear Spoiler' }] },
  interior: { title: 'الداخلية', titleEn: 'Interior', items: [{ id: 'leather_seats', label: 'مقاعد جلد', labelEn: 'Leather Seats' }, { id: 'heated_seats', label: 'مقاعد مدفأة', labelEn: 'Heated Seats' }, { id: 'cooled_seats', label: 'مقاعد مبردة', labelEn: 'Cooled Seats' }, { id: 'navigation', label: 'ملاحة GPS', labelEn: 'Navigation' }, { id: 'bluetooth', label: 'بلوتوث', labelEn: 'Bluetooth' }, { id: 'apple_carplay', label: 'Apple CarPlay', labelEn: 'Apple CarPlay' }, { id: 'android_auto', label: 'Android Auto', labelEn: 'Android Auto' }, { id: 'camera_360', label: 'كاميرا 360°', labelEn: '360° Camera' }] },
  safety: { title: 'الأمان', titleEn: 'Safety', items: [{ id: 'abs', label: 'ABS', labelEn: 'ABS' }, { id: 'airbags', label: 'وسائد هوائية', labelEn: 'Airbags' }, { id: 'blind_spot', label: 'مراقبة النقطة العمياء', labelEn: 'Blind Spot' }, { id: 'lane_warning', label: 'تحذير المسار', labelEn: 'Lane Warning' }, { id: 'cruise_control', label: 'مثبت سرعة', labelEn: 'Cruise Control' }, { id: 'parking_sensors', label: 'حساسات ركن', labelEn: 'Parking Sensors' }, { id: 'awd', label: 'دفع رباعي', labelEn: 'AWD' }] }
};

export default function AddCarPage({ store, language, isDarkMode, onBack, onSuccess, editItem = null, isEditMode = false }) {
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState({ basic: true, overview: true, features: false, contact: false });
  const [images, setImages] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const currentYear = new Date().getFullYear();
  
  // Initialize form with editItem data if in edit mode
  const [form, setForm] = useState(() => {
    if (editItem && isEditMode) {
      return {
        make: editItem.make || '',
        model: editItem.model || '',
        trim: editItem.trim || '',
        year: editItem.year || currentYear,
        price: editItem.price || '',
        priceOnRequest: !!editItem.priceOnRequest,
        mileage: editItem.mileage || '',
        exteriorColor: editItem.exteriorColor || editItem.color || '',
        interiorColor: editItem.interiorColor || '',
        bodyType: editItem.bodyType || 'sedan',
        specs: editItem.specs || editItem.origin || 'gcc',
        steeringSide: editItem.steeringSide || 'left',
        doors: editItem.doors || '4',
        cylinders: editItem.cylinders || '',
        engineCapacity: editItem.engineCapacity || '',
        horsepower: editItem.horsepower || '',
        fuelType: editItem.fuelType || 'petrol',
        transmission: editItem.transmission || 'automatic',
        seatingCapacity: editItem.seatingCapacity || '5',
        sellerType: editItem.sellerType || 'dealer',
        warranty: editItem.hasWarranty ? 'yes' : (editItem.warranty || 'no'),
        warrantyType: editItem.warrantyType || '',
        warrantyProvider: editItem.warrantyProvider || '',
        condition: editItem.condition || 'excellent',
        features: editItem.features || [],
        contactPhone: editItem.contactPhone || store?.phone || '',
        contactWhatsapp: editItem.contactWhatsapp || store?.whatsapp || '',
        contactEmail: editItem.contactEmail || store?.email || '',
        description: editItem.description || ''
      };
    }
    return {
      make: '', model: '', trim: '', year: currentYear, price: '', priceOnRequest: false, mileage: '',
      exteriorColor: '', interiorColor: '', bodyType: 'sedan', specs: 'gcc', steeringSide: 'left', doors: '4',
      cylinders: '', engineCapacity: '', horsepower: '', fuelType: 'petrol', transmission: 'automatic', seatingCapacity: '5',
      sellerType: 'dealer', warranty: 'yes', warrantyType: '', warrantyProvider: '', condition: 'excellent', features: [],
      contactPhone: store?.phone || '', contactWhatsapp: store?.whatsapp || '', contactEmail: store?.email || '', description: ''
    };
  });

  // Initialize images from editItem if in edit mode
  useEffect(() => {
    if (editItem && isEditMode) {
      const existingImages = editItem.images || (editItem.image ? [editItem.image] : []);
      if (existingImages.length > 0) {
        setImages(existingImages.map(url => ({ preview: url, isExisting: true })));
      }
    }
  }, [editItem, isEditMode]);

  const toggle = (s) => setSections(p => ({ ...p, [s]: !p[s] }));
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) { toast.error(language === 'ar' ? 'الحد الأقصى 5 صور' : 'Max 5 images'); return; }
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setImages(prev => [...prev, { file, preview: ev.target.result }]);
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeImage = (idx) => {
    setImages(p => p.filter((_, i) => i !== idx));
    if (mainImageIndex === idx) setMainImageIndex(0);
    else if (mainImageIndex > idx) setMainImageIndex(p => p - 1);
  };

  const handleSubmit = async () => {
    if (!form.make || !form.model) { toast.error(language === 'ar' ? 'يرجى إدخال الماركة والموديل' : 'Please enter make and model'); return; }
    if (!form.priceOnRequest && !form.price) { toast.error(language === 'ar' ? 'يرجى إدخال السعر أو اختيار "اتصل لمعرفة السعر"' : 'Please enter price or choose "Call for price"'); return; }
    // Only require images for new cars, not edits
    if (!isEditMode && images.length === 0) { 
      toast.error(language === 'ar' ? 'يرجى إضافة صورة واحدة على الأقل' : 'Please add at least one image'); 
      return; 
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('business_token');
      const data = new FormData();
      
      if (!isEditMode) {
        data.append('storeType', 'auto');
      }
      
      Object.keys(form).forEach(k => {
        if (k === 'features') data.append(k, JSON.stringify(form[k]));
        else if (k === 'priceOnRequest') data.append(k, form[k] ? 'true' : 'false');
        else if (k === 'price') data.append(k, form.priceOnRequest ? 0 : (form[k] || 0));
        else if (form[k] !== '' && form[k] !== null) data.append(k, form[k]);
      });
      
      // Handle warranty fields
      data.append('hasWarranty', form.warranty === 'yes' ? 'true' : 'false');
      if (form.warrantyType) data.append('warrantyType', form.warrantyType);
      if (form.warrantyProvider) data.append('warrantyProvider', form.warrantyProvider);
      
      // Handle color (map to backend field name)
      if (form.exteriorColor) data.append('color', form.exteriorColor);
      
      // Handle existing images (URLs) vs new images (files)
      const existingUrls = [];
      let newImageIndex = 0;
      images.forEach((img, idx) => {
        if (img.isExisting) {
          existingUrls.push(img.preview);
        } else if (img.file) {
          // Send actual File object, not base64 preview
          if (idx === mainImageIndex) {
            data.append('mainImage', img.file, img.file.name);
          } else {
            data.append(`extraImage${newImageIndex + 1}`, img.file, img.file.name);
            newImageIndex++;
          }
        }
      });
      
      if (existingUrls.length > 0) {
        data.append('existingImages', JSON.stringify(existingUrls));
      }
      
      if (isEditMode && editItem) {
        // PUT request for update
        await axios.put(`${BACKEND_URL}/api/stores/${store.id}/items/${editItem.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(language === 'ar' ? 'تم تحديث السيارة بنجاح' : 'Car updated successfully');
      } else {
        // POST request for create
        await axios.post(`${BACKEND_URL}/api/stores/${store.id}/items`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(language === 'ar' ? 'تم إضافة السيارة بنجاح' : 'Car added successfully');
      }
      
      onSuccess?.();
      onBack?.();
    } catch (error) {
      console.error('Error saving car:', error);
      toast.error(language === 'ar' ? 'حدث خطأ أثناء الحفظ' : 'Error saving car');
    } finally {
      setSaving(false);
    }
  };

  const cls = isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : 'bg-white border-gray-200';
  const labelCls = `block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const yearOptions = [];
  for (let y = currentYear + 1; y >= 1990; y--) yearOptions.push(y);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className={`sticky top-0 z-10 ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'} shadow-sm`}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2 text-[#1E9BD9]"><ArrowLeft className="h-5 w-5" /><span>{language === 'ar' ? 'رجوع' : 'Back'}</span></button>
          <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
            {isEditMode 
              ? (language === 'ar' ? 'تعديل السيارة' : 'Edit Car')
              : (language === 'ar' ? 'إضافة سيارة جديدة' : 'Add New Car')
            }
          </h1>
          <div className="w-20"></div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Images */}
        <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'} shadow-sm`}>
          <h3 className={`font-bold flex items-center gap-2 mb-4 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}><Image className="h-5 w-5 text-[#1E9BD9]" />{language === 'ar' ? `صور السيارة (${images.length}/5)` : `Car Images (${images.length}/5)`}</h3>
          <div className="grid grid-cols-5 gap-3">
            {images.map((img, idx) => (
              <div key={idx} className="relative aspect-square group">
                <img src={img.preview} alt="" className={`w-full h-full object-cover rounded-xl ${mainImageIndex === idx ? 'ring-3 ring-[#1E9BD9]' : ''}`} />
                <button onClick={() => removeImage(idx)} className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100"><X className="h-3 w-3" /></button>
                {mainImageIndex === idx && <div className="absolute bottom-1 left-1 bg-[#1E9BD9] text-white text-xs px-2 py-0.5 rounded">{language === 'ar' ? 'رئيسية' : 'Main'}</div>}
                {mainImageIndex !== idx && <button onClick={() => setMainImageIndex(idx)} className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-2 py-0.5 rounded opacity-0 group-hover:opacity-100">{language === 'ar' ? 'تعيين' : 'Set'}</button>}
              </div>
            ))}
            {images.length < 5 && (
              <label className={`aspect-square border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer ${isDarkMode ? 'border-white/20 hover:border-[#1E9BD9]' : 'border-gray-200 hover:border-[#1E9BD9]'}`}>
                <input type="file" ref={fileInputRef} accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
                <Plus className={`h-8 w-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} /><span className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{language === 'ar' ? 'إضافة' : 'Add'}</span>
              </label>
            )}
          </div>
        </div>

        {/* Basic Info */}
        <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'} shadow-sm`}>
          <button onClick={() => toggle('basic')} className={`w-full flex items-center justify-between p-4 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
            <div className="flex items-center gap-3"><Car className="h-5 w-5 text-[#1E9BD9]" /><span className={`font-bold ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? 'المعلومات الأساسية' : 'Basic Info'}</span></div>
            {sections.basic ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          {sections.basic && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelCls}>{language === 'ar' ? 'الماركة' : 'Make'} *</label><select value={form.make} onChange={(e) => update('make', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}><option value="">{language === 'ar' ? 'اختر' : 'Select'}</option>{CAR_MAKES.map(m => <option key={m} value={m}>{m}</option>)}</select></div>
                <div><label className={labelCls}>{language === 'ar' ? 'الموديل' : 'Model'} *</label><input value={form.model} onChange={(e) => update('model', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} placeholder="Camry" /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className={labelCls}>{language === 'ar' ? 'الفئة' : 'Trim'}</label><input value={form.trim} onChange={(e) => update('trim', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} placeholder="Sport" /></div>
                <div><label className={labelCls}>{language === 'ar' ? 'السنة' : 'Year'}</label><select value={form.year} onChange={(e) => update('year', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}>{yearOptions.map(y => <option key={y} value={y}>{y}</option>)}</select></div>
                <div><label className={labelCls}>{language === 'ar' ? 'المواصفات' : 'Specs'}</label><select value={form.specs} onChange={(e) => update('specs', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}>{SPECS.map(s => <option key={s.value} value={s.value}>{language === 'ar' ? s.label : s.labelEn}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{language === 'ar' ? 'السعر (درهم)' : 'Price (AED)'} *</label>
                  <PriceModePicker
                    value={form.priceOnRequest ? 'onRequest' : 'fixed'}
                    onChange={(m) => update('priceOnRequest', m === 'onRequest')}
                    language={language}
                    isDarkMode={isDarkMode}
                    testIdPrefix="car"
                  />
                  {!form.priceOnRequest && (
                    <input
                      type="number" inputMode="decimal"
                      value={form.price}
                      onChange={(e) => update('price', e.target.value)}
                      className={`w-full mt-2 px-3 py-2.5 border rounded-lg ${cls}`}
                      data-testid="car-price-input"
                    />
                  )}
                </div>
                <div><label className={labelCls}>{language === 'ar' ? 'المسافة (كم)' : 'Mileage (km)'}</label><input type="number" inputMode="decimal" value={form.mileage} onChange={(e) => update('mileage', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} /></div>
              </div>
            </div>
          )}
        </div>

        {/* Overview */}
        <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'} shadow-sm`}>
          <button onClick={() => toggle('overview')} className={`w-full flex items-center justify-between p-4 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
            <div className="flex items-center gap-3"><Gauge className="h-5 w-5 text-[#1E9BD9]" /><span className={`font-bold ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? 'نظرة عامة' : 'Car Overview'}</span></div>
            {sections.overview ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          {sections.overview && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelCls}>{language === 'ar' ? 'لون الخارجية' : 'Exterior Color'}</label><input value={form.exteriorColor} onChange={(e) => update('exteriorColor', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} placeholder={language === 'ar' ? 'أبيض' : 'White'} /></div>
                <div><label className={labelCls}>{language === 'ar' ? 'لون الداخلية' : 'Interior Color'}</label><input value={form.interiorColor} onChange={(e) => update('interiorColor', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} placeholder={language === 'ar' ? 'أسود' : 'Black'} /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className={labelCls}>{language === 'ar' ? 'نوع الهيكل' : 'Body Type'}</label><select value={form.bodyType} onChange={(e) => update('bodyType', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}>{BODY_TYPES.map(b => <option key={b.value} value={b.value}>{language === 'ar' ? b.label : b.labelEn}</option>)}</select></div>
                <div><label className={labelCls}>{language === 'ar' ? 'جهة القيادة' : 'Steering'}</label><select value={form.steeringSide} onChange={(e) => update('steeringSide', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}><option value="left">{language === 'ar' ? 'يسار' : 'Left'}</option><option value="right">{language === 'ar' ? 'يمين' : 'Right'}</option></select></div>
                <div><label className={labelCls}>{language === 'ar' ? 'الأبواب' : 'Doors'}</label><select value={form.doors} onChange={(e) => update('doors', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}>{['2','3','4','5+'].map(d => <option key={d} value={d}>{d}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className={labelCls}>{language === 'ar' ? 'السلندرات' : 'Cylinders'}</label><select value={form.cylinders} onChange={(e) => update('cylinders', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}><option value="">-</option>{['3','4','6','8','10','12'].map(c => <option key={c} value={c}>{c}</option>)}<option value="electric">{language === 'ar' ? 'كهربائي' : 'Electric'}</option></select></div>
                <div><label className={labelCls}>{language === 'ar' ? 'سعة المحرك' : 'Engine (cc)'}</label><input inputMode="numeric" value={form.engineCapacity} onChange={(e) => update('engineCapacity', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} placeholder="2000" /></div>
                <div><label className={labelCls}>{language === 'ar' ? 'القوة' : 'HP'}</label><input inputMode="numeric" value={form.horsepower} onChange={(e) => update('horsepower', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} placeholder="200" /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className={labelCls}>{language === 'ar' ? 'الوقود' : 'Fuel'}</label><select value={form.fuelType} onChange={(e) => update('fuelType', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}><option value="petrol">{language === 'ar' ? 'بنزين' : 'Petrol'}</option><option value="diesel">{language === 'ar' ? 'ديزل' : 'Diesel'}</option><option value="hybrid">{language === 'ar' ? 'هايبرد' : 'Hybrid'}</option><option value="electric">{language === 'ar' ? 'كهربائي' : 'Electric'}</option></select></div>
                <div><label className={labelCls}>{language === 'ar' ? 'ناقل الحركة' : 'Transmission'}</label><select value={form.transmission} onChange={(e) => update('transmission', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}><option value="automatic">{language === 'ar' ? 'أوتوماتيك' : 'Automatic'}</option><option value="manual">{language === 'ar' ? 'يدوي' : 'Manual'}</option></select></div>
                <div><label className={labelCls}>{language === 'ar' ? 'المقاعد' : 'Seats'}</label><select value={form.seatingCapacity} onChange={(e) => update('seatingCapacity', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}>{['2','4','5','7','8+'].map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className={labelCls}>{language === 'ar' ? 'نوع البائع' : 'Seller'}</label><select value={form.sellerType} onChange={(e) => update('sellerType', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}><option value="dealer">{language === 'ar' ? 'تاجر' : 'Dealer'}</option><option value="owner">{language === 'ar' ? 'مالك' : 'Owner'}</option></select></div>
                <div><label className={labelCls}>{language === 'ar' ? 'الضمان' : 'Warranty'}</label><select value={form.warranty} onChange={(e) => update('warranty', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}><option value="yes">{language === 'ar' ? 'نعم' : 'Yes'}</option><option value="no">{language === 'ar' ? 'لا' : 'No'}</option></select></div>
                <div><label className={labelCls}>{language === 'ar' ? 'الحالة' : 'Condition'}</label><select value={form.condition} onChange={(e) => update('condition', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}><option value="excellent">{language === 'ar' ? 'ممتازة' : 'Excellent'}</option><option value="good">{language === 'ar' ? 'جيدة' : 'Good'}</option><option value="fair">{language === 'ar' ? 'مقبولة' : 'Fair'}</option></select></div>
              </div>
              <div><label className={labelCls}>{language === 'ar' ? 'الوصف' : 'Description'}</label><textarea value={form.description} onChange={(e) => update('description', e.target.value)} rows={4} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} placeholder={language === 'ar' ? 'أدخل وصف تفصيلي...' : 'Enter detailed description...'} /></div>
            </div>
          )}
        </div>

        {/* Features */}
        <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'} shadow-sm`}>
          <button onClick={() => toggle('features')} className={`w-full flex items-center justify-between p-4 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
            <div className="flex items-center gap-3"><Settings className="h-5 w-5 text-[#1E9BD9]" /><span className={`font-bold ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? 'المميزات' : 'Features'}</span></div>
            {sections.features ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          {sections.features && (
            <div className="p-4 space-y-6">
              {Object.entries(CAR_FEATURES).map(([cat, data]) => (
                <div key={cat}>
                  <h4 className={`font-bold mb-3 ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? data.title : data.titleEn}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {data.items.map(f => (
                      <button key={f.id} onClick={() => update('features', form.features.includes(f.id) ? form.features.filter(x => x !== f.id) : [...form.features, f.id])}
                        className={`flex items-center gap-2 p-3 rounded-xl border text-sm ${form.features.includes(f.id) ? 'border-[#1E9BD9] bg-[#1E9BD9]/10 text-[#1E9BD9]' : isDarkMode ? 'border-white/10 text-gray-300' : 'border-gray-200'}`}>
                        <span className="flex-1 text-start">{language === 'ar' ? f.label : f.labelEn}</span>{form.features.includes(f.id) && <Check className="h-4 w-4" />}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit — inline at end of form (matches Add Plate/Phone pattern) */}
        <Button
          onClick={handleSubmit}
          disabled={saving}
          className="w-full h-12 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90 text-white rounded-xl text-base font-bold disabled:opacity-50"
          data-testid="add-car-submit-btn"
        >
          {saving
            ? <><Loader2 className="h-5 w-5 animate-spin me-2" />{language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}</>
            : isEditMode
              ? (language === 'ar' ? 'حفظ التعديلات' : 'Save Changes')
              : (language === 'ar' ? 'إضافة السيارة' : 'Add Car')
          }
        </Button>
      </div>
    </div>
  );
}
